<script setup lang="ts">
	import { useDebounceFn } from '@vueuse/core'
	import { computed, reactive, ref, watch } from 'vue'
	import { useRouter } from 'vue-router'

	import BreadCrumb from '@/components/BreadCrumb.vue'
	import LoadingModal from '@/components/LoadingModal.vue'
	import UserApprovalQuickModal from '@/components/dashboard/UserApprovalQuickModal.vue'
	import {
		Dialog,
		DialogContent,
		DialogDescription,
		DialogFooter,
		DialogHeader,
		DialogTitle,
	} from '@/components/ui/dialog'

	import useAuthUser from '@/composables/useAuthUser'
	import useHasPermissionUser from '@/composables/useHasPermissionUser'

	import useCompaniesSearch from '@/modules/companies/composables/useCompaniesSearch'
	import {
		NewDataUser,
		PendingRequest,
	} from '@/modules/pendingRequests/interfaces/pendingRequest'
	import usePendingRequest from '@/modules/pendingRequests/composables/usePendingRequest'
	import PlanComparisonModal from '@/modules/plans/components/PlanComparisonModal.vue'
	import usePlanFeature from '@/modules/plans/composables/usePlanFeature'
	import useFeatureLockCopy from '@/modules/plans/composables/useFeatureLockCopy'
	import useUserApprovals from '@/modules/users/composables/useUserApprovals'

	const { user } = useAuthUser()
	const { hasPermissionUser } = useHasPermissionUser()
	const router = useRouter()

	const pages = [
		{ name: 'Usuarios', to: { name: 'users.index' }, current: false },
		{ name: 'Solicitudes', to: { name: 'users.approvals' }, current: true },
	]

	type TabKey = 'pending' | 'approved' | 'rejected'

	const tabs: { key: TabKey; label: string }[] = [
		{ key: 'pending', label: 'Por autorizar' },
		{ key: 'approved', label: 'Aprobadas' },
		{ key: 'rejected', label: 'No aprobadas' },
	]

	const activeTab = ref<TabKey>('pending')

	const params = reactive<{
		company: number | string | undefined
		search: string
	}>({
		company: user.value?.companyId,
		search: '',
	})

	// Búsqueda por usuario (nombre/correo). Debounce para no pegar al backend por tecla.
	const userSearch = ref('')
	const debouncedUserSearch = useDebounceFn((val: string) => {
		params.search = val
	}, 400)
	watch(userSearch, (val) => debouncedUserSearch(val))

	const { companies, setQuery } = useCompaniesSearch({ immediate: true })

	const {
		userApprovals,
		pages: paginationPages,
		paginations,
		setPage,
		refetchAll,
		isLoading,
		isRefetching,
	} = useUserApprovals({ params })

	// Rechazar directo desde la tabla (al lado de Aprobar). El rechazo de una
	// solicitud de usuario no requiere comentario, así que es una acción directa;
	// rejectingId marca la fila en curso para deshabilitar su botón.
	const { rejectedMutationAsync } = usePendingRequest<NewDataUser>({
		params: {},
		immediate: false,
	})
	const rejectingId = ref<number | null>(null)

	// Confirmación antes de rechazar: el rechazo es una acción directa desde la tabla
	// (sin modal de datos), así que se pide confirmar para no rechazar por error.
	const confirmRejectOpen = ref(false)
	const approvalToReject = ref<PendingRequest<NewDataUser> | null>(null)
	const rejectComment = ref('')
	const rejectError = ref('')

	const askReject = (approval: PendingRequest<NewDataUser>) => {
		approvalToReject.value = approval
		rejectComment.value = ''
		rejectError.value = ''
		confirmRejectOpen.value = true
	}

	const confirmReject = async () => {
		const approval = approvalToReject.value
		if (!approval) return

		// Motivo obligatorio: sin él no tiene sentido registrar el rechazo.
		if (!rejectComment.value.trim()) {
			rejectError.value = 'Escribe el motivo del rechazo.'
			return
		}

		confirmRejectOpen.value = false
		rejectingId.value = approval.id
		try {
			await rejectedMutationAsync({
				params: { id: approval.id, comment: rejectComment.value.trim() },
				config: {
					raiseToast: true,
					onSuccessToast: 'Solicitud rechazada',
					onErrorToast: 'No se pudo rechazar la solicitud',
				},
			})
			refetchAll()
		} finally {
			rejectingId.value = null
			approvalToReject.value = null
		}
	}

	const handlePage = (type: string, newPage: string | number) => {
		const pageNumber = Number(newPage)
		if (paginations.value[type]?.meta?.lastPage) {
			if (
				pageNumber >= 1 &&
				pageNumber <= paginations.value[type].meta.lastPage
			) {
				setPage(type, pageNumber)
			}
		}
	}

	watch(user, () => {
		params.company = user.value?.companyId
	})

	watch(params, () => refetchAll())

	// Flujos de aprobación: función de los planes PRO y BUSINESS+ (el backend
	// responde 422 en todo el flujo). El candado sigue a la empresa SELECCIONADA
	// en el filtro; con plan bloqueado el panel queda con blur + overlay upsell.
	const {
		locked: planLocked,
		subscriptionId,
		currentPlanCode,
	} = usePlanFeature(
		computed(() => params.company),
		'approval_flows'
	)
	const { lockTitle, lockDescription } = useFeatureLockCopy('approval_flows')
	const showCompare = ref(false)
	const canManageBilling = computed(() =>
		hasPermissionUser('update', 'App\\Models\\Company')
	)

	// El swap/pago real vive en la página de suscripción de la empresa; el modal
	// solo elige el plan y ahí se le pasa la mano.
	function onSelectPlan(planCode: string) {
		const targetCompanyId = Number(params.company) || undefined
		if (!targetCompanyId) return
		router.push({
			name: 'company.subscriptions',
			params: { companyId: String(targetCompanyId) },
			query: { planCode },
		})
	}

	// Modal
	const selectedApproval = ref<PendingRequest<NewDataUser> | null>(null)
	const showModal = ref(false)

	const openModal = (approval: PendingRequest<NewDataUser>) => {
		selectedApproval.value = approval
		showModal.value = true
	}

	const closeModal = () => {
		showModal.value = false
		selectedApproval.value = null
	}

	const handleDone = () => {
		closeModal()
		refetchAll()
	}

	// Computed list for active tab
	const activeList = computed(
		() =>
			(userApprovals.value?.[activeTab.value] ??
				[]) as PendingRequest<NewDataUser>[]
	)

	const activePagination = computed(() => paginations.value[activeTab.value])
	const activePage = computed(() => paginationPages.value[activeTab.value] ?? 1)

	const pendingCount = computed(() => userApprovals.value?.pending?.length ?? 0)

	const initials = (approval: PendingRequest<NewDataUser>) =>
		`${approval.newData.name?.charAt(0) ?? ''}${approval.newData.lastname?.charAt(0) ?? ''}`.toUpperCase()

	const formatDate = (dateStr: string) => {
		return new Date(dateStr).toLocaleDateString('es-ES', {
			day: 'numeric',
			month: 'short',
			year: 'numeric',
		})
	}

	const tabBadge: Partial<Record<TabKey, number | undefined>> = {}
</script>

<template>
	<div class="min-h-screen bg-slate-50/50">
		<BreadCrumb :pages="pages" />

		<main class="container mx-auto mt-4 px-4 pb-12">
			<div class="flex flex-col gap-6">
				<!-- Header -->
				<div class="bg-white rounded-xl border shadow-sm p-6">
					<div
						class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4"
					>
						<div>
							<h1 class="text-2xl font-bold tracking-tight text-gray-900">
								Solicitudes de Usuarios
							</h1>
							<p class="text-sm text-muted-foreground mt-1">
								Revisa y aprueba solicitudes de nuevos usuarios
							</p>
						</div>

						<!-- Filtros. El selector de empresa solo aplica a staff interno
						     (ve solicitudes de varias empresas); un usuario de empresa
						     tiene una sola, así que se oculta y queda su búsqueda por usuario. -->
						<div class="flex items-center gap-2">
							<div
								v-if="user?.internal"
								class="relative"
							>
								<svg
									class="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400"
									fill="none"
									stroke="currentColor"
									viewBox="0 0 24 24"
								>
									<path
										stroke-linecap="round"
										stroke-linejoin="round"
										stroke-width="2"
										d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
									/>
								</svg>
								<input
									type="text"
									placeholder="Buscar empresa..."
									class="pl-9 pr-3 py-2 text-sm border border-gray-200 rounded-lg bg-slate-50 focus:bg-white focus:outline-none focus:ring-2 focus:ring-[#021F58]/20 transition-all w-56"
									@input="(e) => setQuery((e.target as HTMLInputElement).value)"
								/>
							</div>

							<select
								v-if="user?.internal"
								v-model="params.company"
								class="text-sm border border-gray-200 rounded-lg px-3 py-2 bg-white text-gray-700 focus:outline-none focus:ring-2 focus:ring-[#021F58]/20 max-w-[200px]"
							>
								<option :value="user?.companyId">Mi empresa</option>
								<option
									v-for="company in companies"
									:key="company.id"
									:value="company.id"
								>
									{{ company.name }}
								</option>
							</select>

							<!-- Búsqueda por el usuario de la solicitud (nombre / correo) -->
							<div class="relative">
								<svg
									class="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400"
									fill="none"
									stroke="currentColor"
									viewBox="0 0 24 24"
								>
									<path
										stroke-linecap="round"
										stroke-linejoin="round"
										stroke-width="2"
										d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
									/>
								</svg>
								<input
									v-model="userSearch"
									type="text"
									placeholder="Buscar por usuario (nombre o correo)..."
									class="pl-9 pr-3 py-2 text-sm border border-gray-200 rounded-lg bg-slate-50 focus:bg-white focus:outline-none focus:ring-2 focus:ring-[#021F58]/20 transition-all w-72"
								/>
							</div>
						</div>
					</div>
				</div>

				<!-- Main panel (blur + overlay cuando el plan no incluye el flujo) -->
				<div class="relative">
					<div
						v-if="planLocked"
						class="absolute inset-0 z-10 flex flex-col items-center justify-center text-center gap-3 rounded-xl bg-white/60 backdrop-blur-[6px] p-8"
					>
						<span class="material-symbols-outlined text-5xl text-gray-500"
							>lock</span
						>
						<h2 class="text-xl font-bold text-gray-900 max-w-lg">
							{{ lockTitle }}
						</h2>
						<p
							v-if="lockDescription"
							class="text-sm text-gray-600 max-w-md"
						>
							{{ lockDescription }}
						</p>
						<button
							class="mt-2 px-4 py-2 rounded-lg bg-[#021F58] text-white text-sm font-medium hover:opacity-90 transition-opacity"
							@click="showCompare = true"
						>
							Ver planes
						</button>
					</div>

					<PlanComparisonModal
						v-model:open="showCompare"
						:company-id="Number(params.company) || undefined"
						:subscription-id="subscriptionId"
						:current-plan-code="currentPlanCode"
						:can-manage="canManageBilling"
						@select="onSelectPlan"
					/>

					<div
						class="bg-white rounded-xl border shadow-sm overflow-hidden"
						:class="planLocked ? 'pointer-events-none select-none' : ''"
						:aria-hidden="planLocked"
					>
						<!-- Tabs -->
						<div class="border-b border-gray-100 px-6">
							<nav class="flex gap-1 -mb-px">
								<button
									v-for="tab in tabs"
									:key="tab.key"
									type="button"
									class="relative px-4 py-3.5 text-sm font-medium transition-colors border-b-2"
									:class="
										activeTab === tab.key ?
											'border-[#021F58] text-[#021F58]'
										:	'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-200'
									"
									@click="activeTab = tab.key"
								>
									{{ tab.label }}
									<span
										v-if="tab.key === 'pending' && pendingCount > 0"
										class="ml-2 inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-700"
									>
										{{ pendingCount }}
									</span>
								</button>
							</nav>
						</div>

						<!-- List -->
						<div class="divide-y divide-gray-50">
							<!-- Loading -->
							<div
								v-if="isLoading"
								class="flex items-center justify-center py-20"
							>
								<div class="flex flex-col items-center gap-3">
									<div
										class="w-8 h-8 border-2 border-gray-100 border-t-[#021F58] rounded-full animate-spin"
									/>
									<p class="text-sm text-gray-400">Cargando solicitudes...</p>
								</div>
							</div>

							<!-- Empty -->
							<div
								v-else-if="activeList.length === 0"
								class="flex flex-col items-center justify-center py-20 text-gray-400"
							>
								<svg
									class="w-14 h-14 mb-4 text-gray-200"
									fill="none"
									stroke="currentColor"
									viewBox="0 0 24 24"
								>
									<path
										stroke-linecap="round"
										stroke-linejoin="round"
										stroke-width="1.5"
										d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
									/>
								</svg>
								<p class="text-base font-medium text-gray-500">
									{{
										activeTab === 'pending' ? 'No hay solicitudes pendientes'
										: activeTab === 'approved' ? 'No hay solicitudes aprobadas'
										: 'No hay solicitudes rechazadas'
									}}
								</p>
								<p class="text-sm text-gray-400 mt-1">Todo al día</p>
							</div>

							<!-- Rows -->
							<div
								v-else
								v-for="approval in activeList"
								:key="approval.id"
								class="flex items-center gap-4 px-6 py-4 hover:bg-gray-50 transition-colors"
							>
								<!-- Avatar -->
								<div
									class="w-10 h-10 rounded-full bg-[#021F58]/10 flex items-center justify-center flex-shrink-0"
								>
									<span class="text-[#021F58] text-sm font-semibold">
										{{ initials(approval) }}
									</span>
								</div>

								<!-- Info -->
								<div class="flex-1 min-w-0">
									<p class="text-sm font-semibold text-gray-900">
										{{ approval.newData.name }} {{ approval.newData.lastname }}
									</p>
									<p class="text-xs text-gray-400 truncate">
										{{ approval.newData.email || 'Sin correo' }}
									</p>
									<p
										v-if="
											approval.state === 'rejected' &&
											approval.newData.comment
										"
										class="text-xs text-red-600 truncate mt-0.5"
									>
										Motivo: {{ approval.newData.comment }}
										<span
											v-if="approval.newData.authorizedName"
											class="text-gray-400"
										>
											· por {{ approval.newData.authorizedName }}
										</span>
									</p>
								</div>

								<!-- Date -->
								<div class="hidden sm:block text-right flex-shrink-0">
									<p class="text-xs text-gray-400">Creado</p>
									<p class="text-xs font-medium text-gray-600">
										{{ formatDate(approval.createdAt) }}
									</p>
								</div>

								<!-- Status badge -->
								<div class="flex-shrink-0">
									<span
										class="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium"
										:class="{
											'bg-amber-50 text-amber-700 border border-amber-200':
												approval.state === 'pending',
											'bg-green-50 text-green-700 border border-green-200':
												approval.state === 'approved',
											'bg-red-50 text-red-700 border border-red-200':
												approval.state === 'rejected',
										}"
									>
										{{
											approval.state === 'pending' ? 'Pendiente'
											: approval.state === 'approved' ? 'Aprobada'
											: 'Rechazada'
										}}
									</span>
								</div>

								<!-- Actions -->
								<div class="flex items-center gap-2 flex-shrink-0">
									<button
										v-if="approval.state === 'pending'"
										type="button"
										class="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium text-white bg-[#021F58] hover:bg-[#1C69A5] transition-colors"
										@click="openModal(approval)"
									>
										<svg
											class="w-3.5 h-3.5"
											fill="none"
											stroke="currentColor"
											viewBox="0 0 24 24"
										>
											<path
												stroke-linecap="round"
												stroke-linejoin="round"
												stroke-width="2"
												d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
											/>
										</svg>
										Aprobar
									</button>

									<!-- Rechazar directo (antes vivía en el modal). El botón "Ver"
									     se quitó: abría la misma vista que el modal de aprobar. -->
									<button
										v-if="approval.state === 'pending'"
										type="button"
										:disabled="rejectingId === approval.id"
										class="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium text-red-600 bg-red-50 hover:bg-red-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
										@click="askReject(approval)"
									>
										<svg
											class="w-3.5 h-3.5"
											fill="none"
											stroke="currentColor"
											viewBox="0 0 24 24"
										>
											<path
												stroke-linecap="round"
												stroke-linejoin="round"
												stroke-width="2"
												d="M6 18L18 6M6 6l12 12"
											/>
										</svg>
										{{ rejectingId === approval.id ? 'Rechazando...' : 'Rechazar' }}
									</button>
								</div>
							</div>
						</div>

						<!-- Pagination -->
						<div
							v-if="activePagination?.meta?.lastPage > 1"
							class="flex items-center justify-between px-6 py-3 border-t border-gray-100 bg-gray-50/50"
						>
							<p class="text-xs text-gray-400">
								Mostrando {{ activePagination.meta.from }}–{{
									activePagination.meta.to
								}}
								de {{ activePagination.meta.total }} resultados
							</p>
							<div class="flex items-center gap-1">
								<button
									type="button"
									class="w-8 h-8 rounded-lg flex items-center justify-center text-gray-500 hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
									:disabled="activePage <= 1"
									@click="handlePage(activeTab, activePage - 1)"
								>
									<svg
										class="w-4 h-4"
										fill="none"
										stroke="currentColor"
										viewBox="0 0 24 24"
									>
										<path
											stroke-linecap="round"
											stroke-linejoin="round"
											stroke-width="2"
											d="M15 19l-7-7 7-7"
										/>
									</svg>
								</button>

								<button
									v-for="p in activePagination.meta.lastPage"
									:key="p"
									type="button"
									class="w-8 h-8 rounded-lg flex items-center justify-center text-xs font-medium transition-colors"
									:class="
										p === activePage ?
											'bg-[#021F58] text-white'
										:	'text-gray-600 hover:bg-gray-100'
									"
									@click="handlePage(activeTab, p)"
								>
									{{ p }}
								</button>

								<button
									type="button"
									class="w-8 h-8 rounded-lg flex items-center justify-center text-gray-500 hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
									:disabled="activePage >= activePagination.meta.lastPage"
									@click="handlePage(activeTab, activePage + 1)"
								>
									<svg
										class="w-4 h-4"
										fill="none"
										stroke="currentColor"
										viewBox="0 0 24 24"
									>
										<path
											stroke-linecap="round"
											stroke-linejoin="round"
											stroke-width="2"
											d="M9 5l7 7-7 7"
										/>
									</svg>
								</button>
							</div>
						</div>
					</div>
				</div>
			</div>
		</main>

		<LoadingModal :when="isLoading || isRefetching" />

		<!-- Confirmación de rechazo -->
		<Dialog v-model:open="confirmRejectOpen">
			<DialogContent class="sm:max-w-md">
				<DialogHeader>
					<DialogTitle>Rechazar solicitud</DialogTitle>
					<DialogDescription>
						¿Seguro que quieres rechazar la solicitud de
						<span class="font-semibold text-gray-900">
							{{ approvalToReject?.newData.name }}
							{{ approvalToReject?.newData.lastname }} </span
						>? Esta acción no se puede deshacer.
					</DialogDescription>
				</DialogHeader>

				<div class="flex flex-col gap-1.5">
					<label
						for="reject-reason"
						class="text-sm font-medium text-gray-700"
					>
						Motivo del rechazo
					</label>
					<textarea
						id="reject-reason"
						v-model="rejectComment"
						rows="3"
						placeholder="Explica por qué se rechaza esta solicitud"
						class="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-red-500/20 focus:border-red-400 resize-none"
						@input="rejectError = ''"
					/>
					<p
						v-if="rejectError"
						class="text-xs text-red-600"
					>
						{{ rejectError }}
					</p>
				</div>
				<DialogFooter class="gap-3 sm:gap-x-3">
					<button
						type="button"
						class="inline-flex items-center justify-center rounded-lg border border-gray-200 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
						@click="confirmRejectOpen = false"
					>
						Cancelar
					</button>
					<button
						type="button"
						class="inline-flex items-center justify-center rounded-lg bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700 transition-colors"
						@click="confirmReject"
					>
						Rechazar solicitud
					</button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	</div>

	<!-- Approval modal -->
	<Teleport to="body">
		<Transition
			enter-active-class="transition-opacity duration-200"
			enter-from-class="opacity-0"
			enter-to-class="opacity-100"
			leave-active-class="transition-opacity duration-150"
			leave-from-class="opacity-100"
			leave-to-class="opacity-0"
		>
			<div
				v-if="showModal && selectedApproval"
				class="fixed inset-0 z-50 flex items-center justify-center p-4"
			>
				<div
					class="absolute inset-0 bg-black/40 backdrop-blur-sm"
					@click="closeModal"
				/>
				<div
					class="relative bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto"
					@click.stop
				>
					<div
						class="flex items-center justify-between px-5 py-4 border-b border-gray-100"
					>
						<div>
							<h2 class="text-base font-semibold text-gray-900">
								Aprobar solicitud
							</h2>
							<p class="text-xs text-gray-400 mt-0.5">
								Asigna los datos del usuario antes de aprobar
							</p>
						</div>
						<button
							type="button"
							class="w-8 h-8 flex items-center justify-center rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
							@click="closeModal"
						>
							<svg
								class="w-5 h-5"
								fill="none"
								stroke="currentColor"
								viewBox="0 0 24 24"
							>
								<path
									stroke-linecap="round"
									stroke-linejoin="round"
									stroke-width="2"
									d="M6 18L18 6M6 6l12 12"
								/>
							</svg>
						</button>
					</div>
					<div class="p-5">
						<UserApprovalQuickModal
							:approval="selectedApproval"
							:company-id="params.company ?? user?.companyId ?? 0"
							@done="handleDone"
							@close="closeModal"
						/>
					</div>
				</div>
			</div>
		</Transition>
	</Teleport>
</template>
